import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../services/api'
import './WelcomeModal.css'

/**
 * WelcomeModal (TQ #7072 Phase 1) — Personal-User Onboarding.
 *
 * Beim Layout-Mount holt die Komponente GET /api/me/welcome-pending. Liefert
 * der Endpoint Karten (aktive gebuchte Services ohne welcome_acks-Zeile), wird
 * pro Service eine Karte angezeigt: Service-Name, kurzer "so findest du es"-Hint
 * und ein Primary-Button auf das Service-Ziel.
 *
 * Beim Schliessen ("Verstanden") POST /api/me/welcome/ack mit allen gezeigten
 * service_types — danach erscheint das Modal nach erneutem Laden nicht wieder
 * (Server filtert acked Services heraus).
 *
 * Phase 2 (TQ #8072, Patric-Business-Input 2026-06-03): Wenn unter den frisch
 * gebuchten Services ein Aria-Modul ist, wird Aria personalisiert in den Vorder-
 * grund gestellt:
 *   1. Statt der generischen Aria-Service-Karte erscheint OBEN ein hervorgehobener,
 *      personalisierter Aria-Begruessungs-Block ("Auto-Hinweis Aria-Tab").
 *   2. Beim Schliessen ("Verstanden") wird — wenn Aria gebucht wurde — direkt der
 *      Aria-Tab geoeffnet ("Aria-Auto-Open"), damit der erste Weg in den Chat fuehrt
 *      (Aria = Leitprodukt). Andere gebuchte Services bleiben als Karten sichtbar.
 *
 * Aria-Erkennung (`isAriaCard`) nutzt dieselbe Keyword-Heuristik wie
 * `presentationFor` (plan_id/item_group enthaelt "ARIA").
 *
 * Backend-Vertrag (services/portal-api/internal/handlers/welcome.go):
 *   GET  /api/me/welcome-pending → { success, cards: ServiceCard[], count }
 *   POST /api/me/welcome/ack     → body { service_types: string[] } → { success, acked }
 *   ServiceCard = { service_type, name, variant_name?, item_group? }
 *   service_type = ERPNext plan_id (z.B. "PERSONAL-STORAGE-MONTHLY"), zugleich Ack-Key.
 */

/** Spiegelt handlers.ServiceCard aus dem Backend (welcome.go). */
export interface WelcomeServiceCard {
  service_type: string
  name: string
  variant_name?: string
  item_group?: string
}

/** Anzeige-Metadaten je Service-Familie: Icon, Erste-Schritte-Hint, Ziel-Link. */
interface ServicePresentation {
  icon: string
  hint: string
  /** In-Cockpit-Ziel relativ zum Cockpit-Root (base wird vom Aufrufer gesetzt). */
  to: string
  /** Button-Beschriftung. */
  cta: string
}

/**
 * Mappt service_type (ERPNext plan_id) bzw. item_group auf Anzeige-Metadaten.
 *
 * Quelle der Ziel-Links: das Cockpit selbst ist der zentrale Einstieg — Services
 * werden über die jeweiligen Cockpit-Tabs erreicht (kein hartkodierter externer
 * Service-Host, da diese pro Tenant/Personal variieren). Aria-Module zeigen auf
 * den Aria-Tab, Mail/Storage/Web auf "Meine Services", Downloads-nahe Produkte auf
 * den Downloads-Tab. Unbekannte Services fallen auf den Services-Tab zurück.
 *
 * Matching ist keyword-basiert über die plan_id (case-insensitive), mit
 * item_group als gröberem Fallback.
 */
function presentationFor(card: WelcomeServiceCard): ServicePresentation {
  const id = (card.service_type || '').toUpperCase()
  const group = (card.item_group || '').toUpperCase()

  const has = (...needles: string[]) =>
    needles.some((n) => id.includes(n) || group.includes(n))

  // Aria-Module zuerst (kann mit MAIL/STORAGE etc. kombiniert sein — der
  // Aria-Tab ist dann der relevante Einstieg).
  if (has('ARIA')) {
    return {
      icon: '🤖',
      hint: 'Deinen KI-Assistenten Aria findest du im Cockpit links unter „Aria" — dort kannst du sofort einen Chat starten.',
      to: '/aria',
      cta: 'Aria öffnen',
    }
  }
  if (has('MAIL')) {
    return {
      icon: '✉️',
      hint: 'Dein Postfach und die Zugangsdaten findest du unter „Meine Services". Webmail erreichst du über den dort verlinkten Zugang.',
      to: '/my-resources',
      cta: 'Zu meinen Services',
    }
  }
  if (has('STORAGE', 'CLOUD')) {
    return {
      icon: '☁️',
      hint: 'Deinen Cloud-Speicher und den Zugangslink findest du unter „Meine Services".',
      to: '/my-resources',
      cta: 'Zu meinen Services',
    }
  }
  if (has('WEB')) {
    return {
      icon: '🌐',
      hint: 'Die Details zu deiner Website findest du unter „Meine Services".',
      to: '/my-resources',
      cta: 'Zu meinen Services',
    }
  }
  if (has('ERP')) {
    return {
      icon: '📊',
      hint: 'Dein ERP-Zugang ist unter „Meine Services" hinterlegt.',
      to: '/my-resources',
      cta: 'Zu meinen Services',
    }
  }
  if (has('PASSWORD', 'BITWARDEN', 'SECRET')) {
    return {
      icon: '🔑',
      hint: 'Dein Passwort-Manager und die Einrichtung sind unter „Lizenzen" bzw. „Downloads" zu finden.',
      to: '/downloads',
      cta: 'Zu den Downloads',
    }
  }
  if (has('HUB', 'AGENT', 'DESKTOP', 'MCP', 'CLIENT', 'DEVICE')) {
    return {
      icon: '💻',
      hint: 'Die zugehörige App bzw. den Agenten lädst du im Bereich „Downloads" herunter.',
      to: '/downloads',
      cta: 'Zu den Downloads',
    }
  }
  // Default: Service-Übersicht.
  return {
    icon: '📦',
    hint: 'Alle Details zu diesem Service findest du unter „Meine Services".',
    to: '/my-resources',
    cta: 'Zu meinen Services',
  }
}

/**
 * True wenn die Karte ein Aria-Modul ist (plan_id oder item_group enthaelt
 * "ARIA"). Identische Heuristik wie der ARIA-Zweig in `presentationFor`, hier
 * fuer das Phase-2-Featuring (hervorgehobener Block + Auto-Open) wiederverwendet.
 */
function isAriaCard(card: WelcomeServiceCard): boolean {
  const id = (card.service_type || '').toUpperCase()
  const group = (card.item_group || '').toUpperCase()
  return id.includes('ARIA') || group.includes('ARIA')
}

function displayName(card: WelcomeServiceCard): string {
  if (card.variant_name && card.variant_name.trim()) {
    return `${card.name} (${card.variant_name})`
  }
  return card.name
}

interface WelcomeModalProps {
  /** Cockpit-Base-Pfad (z.B. "/<token>" oder "" auf Custom-Domain). Wird den
      In-Cockpit-Zielen vorangestellt. */
  base: string
}

export default function WelcomeModal({ base }: WelcomeModalProps) {
  const [cards, setCards] = useState<WelcomeServiceCard[]>([])
  const [open, setOpen] = useState(false)
  const [acking, setAcking] = useState(false)
  const navigate = useNavigate()

  // Beim Mount einmal pending Welcomes laden. Fehler werden bewusst
  // verschluckt (Endpoint nicht erreichbar / kein ERPNext-Customer / 401):
  // ein Onboarding-Modal darf das Cockpit nie blockieren.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const resp = await api.getWelcomePending()
        if (cancelled) return
        if (resp?.success && Array.isArray(resp.cards) && resp.cards.length > 0) {
          setCards(resp.cards)
          setOpen(true)
        }
      } catch {
        // still — kein Modal anzeigen
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  // Schliessen = alle gezeigten service_types acken, dann Modal zu. Nach dem Ack
  // filtert das Backend diese Services aus welcome-pending → Modal kommt nicht
  // wieder. Der Close erfolgt auch wenn der Ack-Call fehlschlägt (kein Stuck-Modal);
  // im Fehlerfall erscheint es beim nächsten Mount erneut, was akzeptabel ist.
  //
  // navigateTo: explizites Ziel (von Karten-CTA-Klick). Ohne Argument greift
  // Phase-2-Aria-Auto-Open falls ein Aria-Modul gebucht wurde. Beides nutzt
  // react-router navigate() statt window.location → kein Seiten-Reload.
  const handleDismiss = useCallback(async (navigateTo?: string) => {
    setAcking(true)
    try {
      const serviceTypes = cards.map((c) => c.service_type).filter(Boolean)
      if (serviceTypes.length > 0) {
        await api.ackWelcome(serviceTypes)
      }
    } catch {
      // ignore — Modal trotzdem schliessen
    } finally {
      setAcking(false)
      setOpen(false)
      // Phase 2 (TQ #8072): Aria-Auto-Open bei explizitem Karten-CTA-Klick
      // (navigateTo gesetzt) direkt dorthin; sonst wenn Aria gebucht → /aria.
      const target =
        navigateTo ??
        (cards.some(isAriaCard)
          ? `${base}/aria`.replace(/\/{2,}/g, '/') || '/'
          : undefined)
      if (target) navigate(target)
    }
  }, [cards, base, navigate])

  if (!open || cards.length === 0) return null

  const joinPath = (to: string) => `${base}${to}`.replace(/\/{2,}/g, '/') || '/'

  // Phase 2 (TQ #8072): Aria nach vorne ziehen. Das erste gebuchte Aria-Modul
  // bekommt einen hervorgehobenen, personalisierten Begruessungs-Block oben; alle
  // uebrigen Services (inkl. evtl. weiterer Aria-Varianten) bleiben als normale
  // Karten darunter — nur die EINE gefeaturete Karte wird aus der Liste entfernt.
  const ariaCard = cards.find(isAriaCard)
  const otherCards = cards.filter((c) => c !== ariaCard)

  return (
    <div className="welcome-overlay" role="dialog" aria-modal="true" aria-labelledby="welcome-title">
      <div className="welcome-modal">
        <div className="welcome-header">
          <h2 id="welcome-title">Willkommen bei IT-SCE 👋</h2>
          <p>
            Schön, dass du da bist! Hier ist ein kurzer Überblick über deine
            {cards.length === 1 ? ' Leistung' : ' Leistungen'} und wo du sie findest.
          </p>
        </div>

        <div className="welcome-body">
          {ariaCard && (
            <div className="welcome-aria-feature">
              <div className="welcome-aria-icon" aria-hidden>
                🤖
              </div>
              <div className="welcome-aria-body">
                <p className="welcome-aria-eyebrow">Dein KI-Assistent ist startklar</p>
                <p className="welcome-aria-title">{displayName(ariaCard)}</p>
                <p className="welcome-aria-hint">
                  Hallo, ich bin <strong>Aria</strong> — deine persönliche KI-Assistentin bei
                  IT-SCE. Ich helfe dir bei Fragen, deinen Diensten und im Alltag. Öffne den
                  Aria-Tab und stell mir gleich deine erste Frage!
                </p>
                <button
                  className="welcome-card-action"
                  onClick={() => handleDismiss(joinPath('/aria'))}
                >
                  Aria öffnen →
                </button>
              </div>
            </div>
          )}
          {otherCards.map((card) => {
            const p = presentationFor(card)
            return (
              <div className="welcome-card" key={card.service_type}>
                <div className="welcome-card-icon" aria-hidden>
                  {p.icon}
                </div>
                <div className="welcome-card-body">
                  <p className="welcome-card-title">{displayName(card)}</p>
                  <p className="welcome-card-hint">{p.hint}</p>
                  <button
                    className="welcome-card-action"
                    onClick={() => handleDismiss(joinPath(p.to))}
                  >
                    {p.cta} →
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        <div className="welcome-actions">
          <button className="welcome-dismiss" onClick={() => handleDismiss()} disabled={acking}>
            {acking ? 'Wird gespeichert…' : 'Verstanden'}
          </button>
        </div>
      </div>
    </div>
  )
}
